// Case study content, adapted from the public, NDA-cleared studies at
// github.com/paplaj-software/enterprise-integration-case-studies. These
// describe real completed work that is already generalized for confidentiality,
// so the NDA disclaimer is carried on each page. Business impact is qualitative
// (as in the source) — no invented metrics.
const SITE = "https://www.paplajsoftware.com";

const NDA_DISCLAIMER =
  "This case study is based on real-world enterprise experience. To respect client confidentiality and NDA obligations, all business details, implementation specifics, architecture diagrams, source code, and identifying information have been intentionally generalized or recreated for demonstration purposes.";

export const caseStudies = {
  "enterprise-integration-apache-camel": {
    slug: "enterprise-integration-apache-camel",
    title: "Enterprise Integration Platform Using Apache Camel",
    summary:
      "A scalable, secure integration platform built with Apache Camel, Spring Boot and Enterprise Integration Patterns.",
    tags: ["Enterprise Integration", "Apache Camel", "Event-Driven"],
    metaTitle:
      "Case Study: Enterprise Integration with Apache Camel | Paplaj Software Services",
    metaDescription:
      "How we built a scalable, secure enterprise integration platform using Apache Camel, Spring Boot and Enterprise Integration Patterns to automate data exchange across business systems.",
    canonical: `${SITE}/case-studies/enterprise-integration-apache-camel`,
    challenge: {
      intro:
        "Organizations operating multiple business applications faced several key obstacles:",
      points: [
        "Integrating diverse systems through standardized interfaces",
        "Eliminating manual data transfers and reconciliation",
        "Supporting both real-time and asynchronous processes",
        "Handling complex data transformations and validations",
        "Ensuring reliability through monitoring, retries and exception handling",
        "Creating a scalable foundation for future integrations"
      ]
    },
    solution:
      "We implemented a scalable enterprise integration platform to enable secure, reliable and automated data exchange between multiple business applications and enterprise systems. The approach leveraged Apache Camel and Enterprise Integration Patterns to orchestrate workflows, transform data, manage asynchronous communication and provide centralized monitoring.",
    architecture: [
      "Business applications and external platforms",
      "REST APIs and SOAP services",
      "Apache Camel integration layer",
      "Message queues",
      "Data transformation services",
      "Monitoring and logging platforms",
      "Enterprise systems (ERP, CRM, workflow platforms)"
    ],
    technologies: [
      { category: "Languages & Frameworks", items: ["Java", "Spring Boot"] },
      { category: "Integration", items: ["Apache Camel"] },
      { category: "APIs", items: ["REST", "SOAP"] },
      { category: "Infrastructure", items: ["Docker", "AWS", "Azure"] },
      { category: "Databases", items: ["Oracle Database", "PostgreSQL"] },
      { category: "Monitoring", items: ["Centralized logging and alerting"] }
    ],
    patterns: [
      "Message Translator",
      "Content-Based Router",
      "Request-Reply",
      "Publish-Subscribe",
      "Dead Letter Channel",
      "Retry Pattern",
      "Idempotent Consumer",
      "Event-Driven Processing"
    ],
    impact: [
      "Automated complex enterprise workflows",
      "Improved system interoperability",
      "Reduced operational overhead",
      "Enhanced reliability and visibility",
      "Built a foundation for future digital initiatives"
    ],
    disclaimer: NDA_DISCLAIMER
  },

  "enterprise-integration-oracle-integration-cloud": {
    slug: "enterprise-integration-oracle-integration-cloud",
    title: "Enterprise Integration Using Oracle Integration Cloud (OIC)",
    summary:
      "Secure, automated integration between external business systems and Oracle Fusion Applications using Oracle Integration Cloud.",
    tags: ["ERP Integration", "Oracle Integration Cloud", "Cloud-Native"],
    metaTitle:
      "Case Study: Oracle Integration Cloud & Oracle Fusion | Paplaj Software Services",
    metaDescription:
      "How we connected external business systems with Oracle Fusion Applications using Oracle Integration Cloud (OIC) for secure, automated, cloud-native data exchange.",
    canonical: `${SITE}/case-studies/enterprise-integration-oracle-integration-cloud`,
    challenge: {
      intro:
        "Organizations integrating multiple business applications with Oracle Fusion ERP faced several obstacles:",
      points: [
        "Connecting disparate systems to Oracle Fusion",
        "Eliminating manual data transfers and reconciliation",
        "Supporting both real-time and scheduled processes",
        "Managing intricate data transformations and validations",
        "Ensuring dependable operations through monitoring and fault management",
        "Establishing a scalable cloud-native integration base"
      ]
    },
    solution:
      "We deployed Oracle Integration Cloud to facilitate secure, reliable and automated data exchange between external business systems and Oracle Fusion Applications through orchestration, transformation, exception management and centralized monitoring.",
    architecture: [
      "External business systems",
      "REST and SOAP APIs",
      "Oracle Integration Cloud (OIC)",
      "Integration flows and orchestration",
      "Data mapping and transformations",
      "Monitoring and tracking dashboard",
      "Oracle Fusion Applications"
    ],
    technologies: [
      {
        category: "Integration Platform",
        items: ["Oracle Integration Cloud (OIC)"]
      },
      { category: "ERP Platform", items: ["Oracle Fusion Applications"] },
      { category: "APIs", items: ["REST", "SOAP"] },
      { category: "Data Formats", items: ["JSON", "XML"] },
      { category: "Security", items: ["OAuth 2.0", "Basic Authentication"] },
      {
        category: "Cloud Platform",
        items: ["Oracle Cloud Infrastructure (OCI)"]
      },
      { category: "Monitoring", items: ["OIC Tracking & Monitoring"] }
    ],
    impact: [
      "Automated critical enterprise workflows",
      "Enhanced interoperability between external systems and Oracle Fusion",
      "Reduced operational overhead",
      "Increased reliability through cloud services",
      "Built a scalable foundation for future initiatives",
      "Enhanced visibility via centralized monitoring"
    ],
    disclaimer: NDA_DISCLAIMER
  }
};

export default caseStudies;
